import { useState } from 'react';

import styled, { keyframes } from 'styled-components';
import ReactMarkdown from 'react-markdown';

import { useLanguage } from '../../Context/LanguageContext';
import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';

import DisplayRenderer from './DisplayRenderer';
import RatingBar from './RatingBar';
import SalesViewRenderer from './SalesViewRenderer';
import RevenueChartRenderer from './RevenueChartRenderer';
import AttendanceChartRenderer from './AttendanceChartRenderer';
import AttendanceRankingListRenderer from './AttendanceRankingListRenderer';
import ClassAttendanceListRenderer from './ClassAttendanceListRenderer';
import EngagementChartRenderer from './EngagementChartRenderer';
import LtvRenderer from './LtvRenderer';
import MemberStatusRenderer from './MemberStatusRenderer';

import LeftArrowLight from '../../image/LeftArrow-light.png';
import AiIconLight from '../../image/ClickAi-light.png';

// [VIZ 연결] vizData.type(핸들러 반환 필드명) → 렌더러 dispatch. 모든 viz 렌더러는 data prop 하나만 받음 (theme는 내부 useTheme). //
function VizDispatch({ vizData }) {
  if (!vizData || !vizData.payload) return null;
  const { type, payload } = vizData;
  if (type === 'attendanceChartResult') return <AttendanceChartRenderer data={payload} />;
  else if (type === 'attendanceRankingResult') return <AttendanceRankingListRenderer data={payload} />;
  else if (type === 'classAttendanceResult') return <ClassAttendanceListRenderer data={payload} />;
  else if (type === 'engagementChartResult') return <EngagementChartRenderer data={payload} />;
  else if (type === 'ltvResult') return <LtvRenderer data={payload} />;
  else if (type === 'memberStatusResult') return <MemberStatusRenderer data={payload} />;
  return null;
}

export default function AiChattingView({
  chatMessages,
  chatInput,
  setChatInput,
  handleSend,
  handleStop,
  isResponding,
  chatMessagesContainerRef,
  onSmsSend,
  onIndividualSmsSend,
  onIndividualSmsGenerate,
  messageRatings,
  onRateMessage,
  isMessageRatable,
}) {
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const [isShowScrollDown, setIsShowScrollDown] = useState(false);

  const hasUserMessages = chatMessages.some(m => !m.isAi);
  const isTypewriting = isResponding && !chatMessages.some(m => m.isLoading);

  // 마지막 AI 메시지 id — 응답 진행 중에는 해당 메시지의 평가 UI 노출 억제 //
  let lastAiMessageId = null;

  for (const msg of chatMessages) {
    if (msg.isAi) lastAiMessageId = msg.id;
  }

  // 평가 UI 노출 여부 — 완료된 AI 응답 + 저장된 turn만 //
  function isShowRatingBar(message) {
    if (message.isLoading || message.isToolLoading) return false;
    if (isResponding && message.id === lastAiMessageId) return false;

    return isMessageRatable(message.id);
  }

  // per-message isSameDisplay 계산 (이전 AI 메시지의 displayItems와 비교) //
  const isSameDisplayMap = (() => {
    const map = {};
    let prevDisplay = null;
    for (const msg of chatMessages) {
      if (msg.isAi && msg.displayItems) {
        const isSame = prevDisplay
          && prevDisplay.totalCount === msg.displayItems.totalCount
          //&& JSON.stringify(prevDisplay.columns) === JSON.stringify(msg.displayItems.columns)
          && JSON.stringify(prevDisplay.items?.map(i => i.memberId))
             === JSON.stringify(msg.displayItems.items?.map(i => i.memberId));
        map[msg.id] = !!isSame;
        prevDisplay = msg.displayItems;
      }
    }
    return map;
  })();

  // display 또는 salesView가 있으면 전체 너비
  function isFullWidth(message) {
    return !!message.displayItems || !!message.salesViewData || !!message.revenueChartData || !!message.vizData;
  }

  // 입력값 변경 //
  function onChangeInput(e) {
    setChatInput(e.target.value);

    e.target.style.height = 'auto';
    e.target.style.height = e.target.scrollHeight + 'px';
    e.target.style.overflowY = e.target.scrollHeight > 165 ? 'auto' : 'hidden';
  }

  // 엔터 키 입력 방지 //
  function onKeyDownInput(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
    }
  }

  // 엔터 키 입력 시 전송 //
  function onKeyUpInput(e) {
    if (e.key === 'Enter' && !e.shiftKey && !isResponding) {
      handleSend();
    }
  }

  // 메시지 목록 스크롤 //
  function onScrollMessageList() {
    const el = chatMessagesContainerRef.current;
    if (!el) return;
    const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    setIsShowScrollDown(!isNearBottom);
  }

  // 하단으로 스크롤 //
  function onClickScrollToBottom() {
    const el = chatMessagesContainerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }

  // 전송/중단 버튼 클릭 //
  function onClickSendButton() {
    if (isResponding) {
      if (isTypewriting) handleStop();
    }
    else {
      handleSend();
    }
  }

  return (
    <ChattingWrapper>
      {hasUserMessages ? (
        <MessageListWrapper ref={chatMessagesContainerRef} onScroll={onScrollMessageList}>
          {chatMessages.map((message) => (
            <MessageRow key={message.id} $isAi={message.isAi} data-message-id={message.id}>
              {message.isAi ? (
                message.text || message.isLoading || message.isToolLoading || message.revenueChartData || message.salesViewData || message.displayItems || message.vizData ? (
                <AiMessageAppear $fullWidth={isFullWidth(message)}>
                  <AiMessageWrapper $fullWidth={isFullWidth(message)}>
                    <AiMessageHeader>
                      <AiIconBox $bg={theme.primary}>
                        <Image width={22} height={22} src={AiIconLight} />
                      </AiIconBox>
                      <AiLabelBox $bg={theme.primaryAlpha50}>
                        <Text fontFamily='NotoSansKR-Bold' fontSize={14} color={isDark ? theme.text : theme.white} cursor='default'>LINKO AI Manager</Text>
                      </AiLabelBox>
                    </AiMessageHeader>
                    <AiContentBox $bg={isDark ? theme.background : theme.white} $fullWidth={isFullWidth(message)}>
                      {message.isLoading ? (
                        <WaveDots>
                          <Dot color={theme.primaryAlpha50} style={{ animationDelay: '0ms' }} />
                          <Dot color={theme.primaryAlpha50} style={{ animationDelay: '400ms' }} />
                          <Dot color={theme.primaryAlpha50} style={{ animationDelay: '800ms' }} />
                        </WaveDots>
                      ) : message.isToolLoading ? (
                        <ShimmerText color={theme.primary}>{message.text}</ShimmerText>
                      ) : message.revenueChartData ? (
                        <RevenueChartRenderer data={message.revenueChartData} />
                      ) : message.salesViewData ? (
                        <SalesViewRenderer data={message.salesViewData} />
                      ) : message.vizData ? (
                        <VizDispatch vizData={message.vizData} />
                      ) : message.displayItems ? (
                        // [MULTI-AXIS DISPLAY] primary 표 + 추가 축 표(extraDisplays) 순차 렌더. SMS는 primary에만.
                        <MultiDisplayWrapper>
                          <DisplayRenderer
                            displayItems={message.displayItems}
                            theme={theme}
                            isDark={isDark}
                            isNewResponse={message.isNewResponse}
                            isSameDisplay={isSameDisplayMap[message.id] || false}
                            smsRecommendation={message.smsRecommendation}
                            onSmsSend={(templateId, text, recipients) => onSmsSend(message.id, templateId, text, recipients)}
                            onIndividualSmsSend={(memberId, text) => onIndividualSmsSend(message.id, memberId, text)}
                            onIndividualSmsGenerate={(memberId, recipient, context) => onIndividualSmsGenerate(message.id, recipient, context)}
                          />
                          {Array.isArray(message.extraDisplays) && message.extraDisplays.map((extra, _ei) => (
                            <DisplayRenderer
                              key={`${message.id}_extra_${_ei}`}
                              displayItems={extra}
                              theme={theme}
                              isDark={isDark}
                              isNewResponse={false}
                              isSameDisplay={false}
                            />
                          ))}
                        </MultiDisplayWrapper>
                      ) : (
                        <TextBox $bg={isDark ? theme.background : theme.white}>
                          <MarkdownText color={theme.text}>
                            <ReactMarkdown>{message.text}</ReactMarkdown>
                          </MarkdownText>
                        </TextBox>
                      )}
                    </AiContentBox>
                    {
                      isShowRatingBar(message) &&
                      <RatingBar
                        rating={messageRatings[message.id] || null}
                        onRate={(score, reason, comment) => onRateMessage(message.id, score, reason, comment)} />
                    }
                  </AiMessageWrapper>
                </AiMessageAppear>
                ) : null
              ) : (
                <MessageBubble $primary={theme.primary}>
                  <MessageText color={isDark ? theme.text : theme.white}>{message.text}</MessageText>
                </MessageBubble>
              )}
            </MessageRow>
          ))}
        </MessageListWrapper>
      )
      :
      (
        <EmptySpacer>
          <Text fontFamily='NotoSansKR-Medium' fontSize={18} color={theme.gray} cursor='default'>{t('aiManager.greeting')}</Text>
        </EmptySpacer>
      )}

      {hasUserMessages && (
        <ScrollDownButton $show={isShowScrollDown} $bg={theme.primaryAlpha50} onClick={onClickScrollToBottom}>
          <Image width={12} height={18} src={LeftArrowLight} style={{ transform: 'rotate(270deg)', marginTop: '3px' }} />
        </ScrollDownButton>
      )}

      <ChattingBottomWrapper backgroundColor={theme.background}>
        <ChattingInputBox backgroundColor={isDark ? theme.subBackground : theme.primaryAlpha10}>
          <ChattingInput
            rows={1}
            value={chatInput}
            onChange={onChangeInput}
            onKeyDown={onKeyDownInput}
            onKeyUp={onKeyUpInput}
            placeholder={t('aiManager.inputPlaceholder')}
            color={theme.text}
            $placeholderColor={theme.subText}
            scrollThumbColor={theme.background}
          />
        </ChattingInputBox>
        <ChattingSendButton onClick={onClickSendButton} $disabled={isResponding && !isTypewriting} $bg={theme.primary}>
          {isResponding ? <StopIcon $color={theme.white} /> : <Image width={11} height={16} src={LeftArrowLight} style={{ transform: 'rotate(90deg)' }} />}
        </ChattingSendButton>
      </ChattingBottomWrapper>
    </ChattingWrapper>
  );
}

const ChattingWrapper = styled.div`
  position: relative;

  display: flex;
  flex-direction: column;

  width: 100%;
  min-width: 430px;
  max-width: 1000px;
  height: 100%;
  margin: 0 auto;

  box-sizing: border-box;
`;

const ChattingBottomWrapper = styled.div`
  display: flex;

  padding: 40px 0 20px 0;

  width: 100%;

  gap: 10px;

  box-sizing: border-box;
  background-color: ${props => props.backgroundColor};
`;

const ChattingInputBox = styled.div`
  display: flex;

  justify-content: flex-start;
  align-items: center;

  padding: 7.5px 15px 7.5px 15px;

  width: 100%;
  min-height: 45px;
  max-height: 200px;

  border-radius: 8px;

  box-sizing: border-box;

  background-color: ${props => props.backgroundColor};
`;

const ChattingInput = styled.textarea`
  width: 100%;
  height: auto;
  max-height: 165px;

  font-family: NotoSansKR-Regular;
  font-size: 16px;
  line-height: 22px;

  overflow-y: hidden;

  color: ${props => props.color};

  background-color: transparent;

  border: none;
  outline: none;
  resize: none;

  margin: 0;
  padding: 0;

  text-align: left;

  &::placeholder {
    color: ${props => props.$placeholderColor};
  }

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.scrollThumbColor};
    border-radius: 4px;
  }

  scrollbar-width: thin;
  scrollbar-color: ${props => props.scrollThumbColor} transparent;
`;

const ChattingSendButton = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  width: 45px;
  height: 45px;

  border-radius: 10px;

  background-color: ${props => props.$bg};

  opacity: ${props => props.$disabled ? 0.5 : 1};

  &:hover {
    cursor: ${props => props.$disabled ? 'default' : 'pointer'};
  }
`;

const StopIcon = styled.div`
  width: 13px;
  height: 13px;
  background-color: ${props => props.$color};
  border-radius: 2px;
`;

const MessageListWrapper = styled.div`
  display: flex;
  flex-direction: column;

  flex: 1;

  overflow-y: auto;

  gap: 12px;
  padding: 10px 10px 10px 10px;

  &::-webkit-scrollbar {
    width: 2px;
    height: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme.scrollBar};
    border-radius: 2px;
  }

  &::-webkit-scrollbar-corner {
    background: transparent;
  }

  &::after {
    content: '';
    display: block;
    flex-shrink: 0;
    height: var(--after-spacer-height, 0px);
  }
`;

const EmptySpacer = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  flex: 1;
`;

const MessageRow = styled.div`
  display: flex;

  justify-content: ${props => props.$isAi ? 'flex-start' : 'flex-end'};

  width: ${props => props.$isAi ? '100%' : 'auto'};

  padding: 0 0 50px 0;
`;

const AiMessageWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: ${props => props.$fullWidth ? '100%' : 'fit-content'};
  max-width: 100%;

  gap: 5px;
`;

const AiMessageHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  height: 30px;

  gap: 5px;
`;

const AiIconBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  width: 40px;
  height: 30px;

  background: ${props => props.$bg};
  border-radius: 5px;
`;

const AiLabelBox = styled.div`
  display: flex;
  align-items: center;

  padding: 0 10px;
  height: 30px;

  background: ${props => props.$bg};
  border-radius: 5px;
`;

const MultiDisplayWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;

  gap: 10px;
`;

const AiContentBox = styled.div`
  display: flex;
  flex-direction: ${props => props.$fullWidth ? 'column' : 'row'};
  align-items: flex-start;

  padding: ${props => props.$fullWidth ? '0' : '5px 10px'};
  width: ${props => props.$fullWidth ? '100%' : 'fit-content'};
  max-width: 100%;

  background: ${props => props.$fullWidth ? 'transparent' : props.$bg};
  border-radius: ${props => props.$fullWidth ? '0' : '10px'};

  box-sizing: border-box;
  gap: ${props => props.$fullWidth ? '10px' : '0'};
`;

const MessageBubble = styled.div`
  position: relative;

  max-width: 75%;
  padding: 10px;

  border-radius: 5px;

  background-color: ${props => props.$primary};

  word-break: break-word;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    right: 5px;
    width: 0;
    height: 0;
    border-top: 10px solid ${props => props.$primary};
    border-left: 10px solid transparent;
  }
`;

const TextBox = styled.div`
  padding: 5px 10px;
  width: fit-content;
  max-width: 100%;

  background: ${props => props.$bg};
  border-radius: 10px;

  box-sizing: border-box;
`;

const MessageText = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 14px;
  line-height: 22px;

  color: ${props => props.color};

  white-space: pre-wrap;
  margin: 0;
`;

const MarkdownText = styled.div`
  font-family: NotoSansKR-Regular;
  font-size: 14px;
  line-height: 22px;
  color: ${props => props.color};
  margin: 0;

  p { margin: 4px 0; }
  p:first-child { margin-top: 0; }
  p:last-child { margin-bottom: 0; }
  strong { font-family: NotoSansKR-Bold; }
  ul, ol { margin: 4px 0; padding-left: 20px; }
  li { margin: 2px 0; }
`;

const appearAnimation = keyframes`
  0% { opacity: 0; transform: translateY(10px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const AiMessageAppear = styled.div`
  width: ${props => props.$fullWidth ? '100%' : 'fit-content'};
  max-width: 100%;
  animation: ${appearAnimation} 0.3s ease-out forwards;
`;

const shimmerSweep = keyframes`
  0% { background-position: 200% center; }
  100% { background-position: -200% center; }
`;

const ShimmerText = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;
  line-height: 22px;

  white-space: pre-wrap;
  margin: 0;

  background: linear-gradient(
    90deg,
    ${props => props.color}44 0%,
    ${props => props.color}44 30%,
    ${props => props.color}A3 46%,
    ${props => props.color}C7 50%,
    ${props => props.color}A3 54%,
    ${props => props.color}44 70%,
    ${props => props.color}44 100%
  );
  background-size: 200% 100%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${shimmerSweep} 2.8s linear infinite;
`;

const waveAnimation = keyframes`
  0% { transform: translateY(0); }
  8% { transform: translateY(-6px); }
  17% { transform: translateY(0); }
  100% { transform: translateY(0); }
`;

const WaveDots = styled.div`
  display: flex;

  justify-content: flex-end;
  align-items: flex-end;

  padding: 4px 0;
  margin-top: 5px;

  gap: 4px;
`;

const Dot = styled.span`
  width: 6px;
  height: 6px;

  border-radius: 50%;

  background-color: ${props => props.color};

  animation: ${waveAnimation} 1.8s ease-in-out infinite;
`;

const ScrollDownButton = styled.div`
  position: absolute;
  bottom: 110px;
  left: 50%;
  transform: translateX(-50%);

  display: flex;
  justify-content: center;
  align-items: center;

  width: 36px;
  height: 36px;

  border-radius: 50%;
  background-color: ${props => props.$bg};

  opacity: ${props => props.$show ? 1 : 0};
  pointer-events: ${props => props.$show ? 'auto' : 'none'};
  transition: opacity 0.3s ease-in-out;

  z-index: 10;

  &:hover {
    cursor: pointer;
  }
`;

const Image = styled.img`
  width: ${props => props.width}px;
  height: ${props => props.height}px;

  object-fit: contain;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0;

  cursor: ${props => props.cursor};
`;
